import dayjs from 'dayjs';
import { OnboardingModulesTypes } from '@frontend/api-onboarding-modules';
import { OnboardingTasksV2 } from '@frontend/api-onboarding-tasks';
import { i18next } from '@frontend/i18n';
import { IconName } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { AppointmentDateFormat, SchedulingTypeMetaDataKeys } from './constants';
import { ModuleTaskStats, ScheduledAppointmentData } from './types';

export const getTaskStatusIcon = (
  task: OnboardingTasksV2.Types.TaskRow,
  moduleTasks: OnboardingTasksV2.Types.ModuleRow[]
): { icon: IconName; color: string } => {
  if (isTaskCompleted(task)) {
    return { icon: 'check-small', color: theme.colors.success40 };
  }
  if (isTaskBlocked(task, moduleTasks)) {
    return { icon: 'error-badge-small', color: theme.colors.neutral20 };
  }
  if (isScheduledTask(task)) {
    return { icon: 'calendar-small', color: theme.colors.neutral20 };
  }

  return { icon: 'in-progress-small', color: theme.colors.neutral20 };
};

export const isScheduledTask = (task: OnboardingTasksV2.Types.TaskRow): boolean => {
  return !!task.meta?.find(({ key, value }) => SchedulingTypeMetaDataKeys.includes(key) && value.length > 0);
};

export const getModuleTaskStats = (moduleList: OnboardingTasksV2.Types.ModuleRow[] = []) => {
  const stats: ModuleTaskStats = {
    scheduled: 0,
    pending: 0,
    pendingRequired: 0,
    completed: 0,
    total: 0,
    progress: 0,
    hasTaskStarted: false,
  };
  moduleList.forEach((module) => {
    module.tasks.forEach((task) => {
      if (isTaskCompleted(task)) {
        stats.completed += 1;
      } else {
        stats.pending += 1;
        stats.pendingRequired += task.isOptional ? 0 : 1;
        stats.scheduled += isScheduledTask(task) ? 1 : 0;
      }
      stats.total += 1;
    });
  });
  stats.progress = stats.total > 0 ? (stats.completed / stats.total) * 100 : 0;
  stats.hasTaskStarted = stats.completed + stats.scheduled > 0;
  return stats;
};

export const getScheduledAppointmentList = (
  moduleList: OnboardingTasksV2.Types.ModuleRow[] = []
): ScheduledAppointmentData[] => {
  return moduleList
    .flatMap(({ tasks }) => tasks)
    .filter(isScheduledTask)
    .flatMap((task) => {
      const appointmentsList: ScheduledAppointmentData[] = [];
      (task.meta ?? [])
        .filter(({ key }) => SchedulingTypeMetaDataKeys.includes(key))
        .flatMap(({ key, value: valueList }) => valueList.map((value) => ({ key, value })))
        .forEach(({ key, value }) => {
          const existingAppointmentIndex = appointmentsList.findIndex((appointment) => appointment.date === value);
          if (existingAppointmentIndex === -1) {
            appointmentsList.push({
              date: value,
              title: task.taskTitle,
              type: key as OnboardingTasksV2.Types.MetaDataKeys,
              taskId: task.id,
              count: 1,
            });
          } else {
            appointmentsList[existingAppointmentIndex].count += 1;
          }
        });

      return appointmentsList.map((appointment) => ({
        ...appointment,
        title: getScheduledAppointmentTitle(appointment),
      }));
    })
    .sort((a, b) => dayjs(a.date).diff(dayjs(b.date)));
};

export const getTaskById = (taskId: number, moduleTasks: OnboardingTasksV2.Types.ModuleRow[]) => {
  return moduleTasks.flatMap(({ tasks }) => tasks).find((task) => task.id === taskId);
};

export const getBlockingTasks = (
  task: OnboardingTasksV2.Types.TaskRow,
  moduleTasks: OnboardingTasksV2.Types.ModuleRow[]
) => {
  return (task.prerequisiteTaskIds ?? [])
    .map((id) => getTaskById(id, moduleTasks))
    .filter((task) => task && !isTaskCompleted(task));
};

export const isTaskBlocked = (
  task: OnboardingTasksV2.Types.TaskRow,
  moduleTasks: OnboardingTasksV2.Types.ModuleRow[]
) => {
  return !isTaskCompleted(task) && getBlockingTasks(task, moduleTasks).length > 0;
};

export const isTaskCompleted = (task: OnboardingTasksV2.Types.TaskRow) => {
  return task.status === OnboardingTasksV2.Types.TaskStatus.COMPLETE;
};

export const formatAppointmentDate = (date: string, dateFormat = AppointmentDateFormat.DEFAULT): string => {
  return dayjs(date).format(dateFormat);
};

export const getTaskScheduleDates = (task: OnboardingTasksV2.Types.TaskRow): string[] | undefined => {
  if (isScheduledTask(task) && task.meta) {
    const metaDateKeyValueMap = task.meta.reduce((acc, { key, value }) => {
      acc[key] = value;
      return acc;
    }, {} as Partial<Record<OnboardingTasksV2.Types.MetaDataKeys, string[]>>);

    switch (task.id) {
      case OnboardingModulesTypes.TaskIds.PortDate:
        return metaDateKeyValueMap[OnboardingTasksV2.Types.MetaDataKeys.PORT_DATES];
      case OnboardingModulesTypes.TaskIds.ScheduleCallWithOnboarder:
        return metaDateKeyValueMap[OnboardingTasksV2.Types.MetaDataKeys.SCHEDULE_CALL_DATE];
      case OnboardingModulesTypes.TaskIds.PhonesShipped:
        return metaDateKeyValueMap[OnboardingTasksV2.Types.MetaDataKeys.PHONES_SHIPPED_DATE];
      default:
        return metaDateKeyValueMap[OnboardingTasksV2.Types.MetaDataKeys.META_DATA_UNKNOWN];
    }
  }
  return undefined;
};

export const getScheduledAppointmentTitle = (appointment: ScheduledAppointmentData) => {
  switch (appointment.type) {
    case OnboardingTasksV2.Types.MetaDataKeys.PHONES_SHIPPED_DATE:
      return i18next.t('{{count}} Phones Shipped', { count: appointment.count, ns: 'onboardingDashboard' });
    case OnboardingTasksV2.Types.MetaDataKeys.SCHEDULE_CALL_DATE:
      return i18next.t('{{count}} Onboarding Call', { count: appointment.count, ns: 'onboardingDashboard' });
    case OnboardingTasksV2.Types.MetaDataKeys.PORT_DATES:
      return i18next.t('{{count}} Numbers Porting', { count: appointment.count, ns: 'onboardingDashboard' });
    default:
      return appointment.title;
  }
};

export const getAppointmentTypeIcon = (type: OnboardingTasksV2.Types.MetaDataKeys): IconName => {
  switch (type) {
    case OnboardingTasksV2.Types.MetaDataKeys.PORT_DATES:
      return 'porting-status-small';
    default:
      return 'phone-small';
  }
};
